/**
 * Sector operations — Rosters, Shifts, Duty Roles, Changes, and Attendance data model and pure validators.
 * R39 A6.a (plan docs/plans/sector-operations.md §2 SO-D5, SO-D14, SO-P7, SO-P16, SO-P60; §4 ops_shifts, ops_rosters, ops_roster_changes, ops_attendance).
 */

import { formatZonedDateKey, zonedTimeToUtc } from '../charts/engine/bucket.js'
import { resolveWorkspaceTimeZone } from '../repeat/keys.js'
import type { OpsSector } from './places.js'
import type { HospitalStaffGroup } from './hosPeople.js'
import { parseCsvText } from './csv.js'
import { holdsLegalRole, type OpsComplianceItem } from './compliance.js'

export const OPS_ATTENDANCE_LABEL = 'operational attendance'

export const DUTY_ROLE_KEY_REGEX = /^[a-z0-9_]{1,40}$/
export const DEPARTMENT_KEY_REGEX = /^[A-Z0-9_]{1,40}$/

export function validateDepartmentKey(key: unknown): string {
  if (typeof key !== 'string' || !DEPARTMENT_KEY_REGEX.test(key.trim())) {
    throw new Error(
      `Invalid department key "${String(key)}". Key must be upper snake case and <= 40 characters (^[A-Z0-9_]{1,40}$).`,
    )
  }
  return key.trim()
}

export function validateDepartmentLabel(label: unknown): string {
  if (typeof label !== 'string' || label.trim().length === 0 || label.trim().length > 100) {
    throw new Error('Department label must be between 1 and 100 characters.')
  }
  return label.trim()
}

export const MAX_ROSTER_PERIOD_DAYS = 42
export const MAX_ROSTER_ASSIGNMENTS = 2000
export const MAX_ATTENDANCE_CSV_ROWS = 2000
export const OPS_ATTENDANCE_MAX_CSV_ROWS = 2000

export const OPS_ASSIGNMENT_KINDS = [
  'SHIFT',
  'ON_CALL_PRIMARY',
  'ON_CALL_BACKUP',
] as const

export type OpsAssignmentKind = (typeof OPS_ASSIGNMENT_KINDS)[number]

export const OPS_ROSTER_WARNING_CODES = [
  'OVERLAP',
  'DOUBLE_BOOKED',
  'MISSING_COVER',
  'SKILL_MISSING',
  'ON_LEAVE',
  'LEGAL_ROLE_MISSING',
] as const

export type OpsRosterWarningCode = (typeof OPS_ROSTER_WARNING_CODES)[number]

export function isBlockingRosterWarning(code: unknown): boolean {
  return code === 'LEGAL_ROLE_MISSING'
}

export const OPS_ROSTER_CHANGE_TYPES = [
  'LEAVE',
  'SWAP',
  'ON_CALL_SWAP',
  'RELIEF',
] as const

export type OpsRosterChangeType = (typeof OPS_ROSTER_CHANGE_TYPES)[number]

export const OPS_ROSTER_CHANGE_STATES = [
  'REQUESTED',
  'ACCEPTED',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
] as const

export type OpsRosterChangeState = (typeof OPS_ROSTER_CHANGE_STATES)[number]

export const OPS_ATTENDANCE_STATUSES = [
  'PRESENT',
  'ABSENT',
  'LATE',
  'HALF_DAY',
  'ON_LEAVE',
] as const

export type OpsAttendanceStatus = (typeof OPS_ATTENDANCE_STATUSES)[number]

export const OPS_ATTENDANCE_SOURCES = [
  'PROXY',
  'IMPORT',
  'CLOCK',
  'QR',
  'PROXY_PEER',
] as const

export type OpsAttendanceSource = (typeof OPS_ATTENDANCE_SOURCES)[number]

export const OPS_ATTENDANCE_PROXY_STATES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
] as const

export type OpsAttendanceProxyState =
  (typeof OPS_ATTENDANCE_PROXY_STATES)[number]

export const OPS_ATTENDANCE_CORRECTION_STATES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
] as const

export type OpsAttendanceCorrectionState =
  (typeof OPS_ATTENDANCE_CORRECTION_STATES)[number]

// ---------------------------------------------------------------------------
// Model Types
// ---------------------------------------------------------------------------

export interface OpsDutyRole {
  workspaceId: string
  key: string
  label: string
  sector: OpsSector | 'core'
  department?: string | null
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsDepartmentEntry {
  key: string
  label: string
  archived: boolean
}

export interface OpsWorkspaceDepartmentsDoc {
  workspaceId: string
  departments: OpsDepartmentEntry[]
  version: number
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsShift {
  id: string
  workspaceId: string
  name: string
  code: string
  startMinute: number
  endMinute: number
  durationMinutes?: number
  dutyRoleKeys: string[]
  placeIds: string[]
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsCoverageRule {
  id: string
  workspaceId: string
  placeId: string
  shiftId: string
  dutyRoleKey: string
  minCount: number
  requiredSkill?: string | null
  legalRoleKey?: string | null
  staffGroup?: HospitalStaffGroup | null
  archived: boolean
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export type OpsPersonRef =
  | { kind: 'MEMBER'; uid: string }
  | { kind: 'WORKER'; workerId: string }

export interface OpsRosterAssignment {
  id: string
  dayKey: string
  shiftId: string
  personRef: OpsPersonRef
  dutyRoleKey: string
  kind: OpsAssignmentKind
}

export interface OpsRosterWarning {
  code: OpsRosterWarningCode
  message: string
  personRef?: OpsPersonRef
  dayKey?: string
  shiftId?: string
  conflictingShiftId?: string
  conflictingDayKey?: string
  otherRosterId?: string
  placeId?: string
  dutyRoleKey?: string
  minCount?: number
  actualCount?: number
  requiredSkill?: string
  legalRoleKey?: string
  staffGroup?: string
}

export interface OpsRoster {
  id: string
  workspaceId: string
  placeId: string
  periodStart: string
  periodEnd: string
  draftAssignments: OpsRosterAssignment[]
  publishedVersion: number
  readAccess: string[] | null
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsRosterLegalOverride {
  reason: string
  byUid: string
  at: unknown
  gaps: OpsRosterWarning[]
}

export interface OpsRosterVersion {
  version: number
  rosterId: string
  workspaceId: string
  placeId: string
  sector?: OpsSector | 'core'
  periodStart: string
  periodEnd: string
  readAccess: string[] | null
  assignments: OpsRosterAssignment[]
  warnings: OpsRosterWarning[]
  acks: Record<string, string>
  changeId?: string | null
  legalOverride?: OpsRosterLegalOverride | null
  publishedBy: string
  publishedAt: unknown
  createdAt?: unknown
  createdBy?: string
}

export interface OpsRosterChange {
  id: string
  workspaceId: string
  placeId?: string
  rosterId?: string
  type: OpsRosterChangeType
  state: OpsRosterChangeState
  requesterUid: string
  requesterPersonRef: OpsPersonRef
  targetPersonRef?: OpsPersonRef | null
  partyUids: string[]
  dayKey?: string
  endDayKey?: string
  shiftId?: string
  replacementShiftId?: string
  dutyRoleKey?: string
  requesterAssignmentId?: string | null
  targetAssignmentId?: string | null
  reliefFor?: OpsPersonRef | null
  requestId?: string | null
  reason?: string
  responseNote?: string
  decisionReason?: string
  selfApproved?: boolean
  decidedBy?: string | null
  decidedAt?: unknown
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsAttendance {
  id: string
  workspaceId: string
  dayKey: string
  personKey: string
  personRef: OpsPersonRef
  personUid?: string | null
  shiftId: string
  status: OpsAttendanceStatus
  source: OpsAttendanceSource
  proxyState?: OpsAttendanceProxyState | null
  rejectionReason?: string | null
  decidedBy?: string | null
  decidedAt?: unknown
  markedBy: string
  markedAt: unknown
  actualIn?: string | null
  actualOut?: string | null
  locked: boolean
  label?: string
  createdAt?: unknown
  createdBy?: string
  updatedAt?: unknown
  updatedBy?: string
}

export interface OpsAttendanceCorrection {
  id: string
  attendanceId: string
  workspaceId: string
  personUid?: string | null
  before?: {
    status: OpsAttendanceStatus
    actualIn?: string | null
    actualOut?: string | null
  }
  after?: {
    status: OpsAttendanceStatus
    actualIn?: string | null
    actualOut?: string | null
  }
  requestedStatus: OpsAttendanceStatus
  requestedActualIn?: string | null
  requestedActualOut?: string | null
  reason: string
  requestedBy: string
  requestedAt: unknown
  state: OpsAttendanceCorrectionState
  decidedBy?: string | null
  decidedAt?: unknown
  decisionReason?: string
  selfApproved?: boolean
}

export interface OpsOnDutyHop {
  level: 'ON_SHIFT' | 'ON_CALL_PRIMARY' | 'ON_CALL_BACKUP' | 'PLACE_OWNER'
  personRefs?: OpsPersonRef[]
  ownerRoleKey?: string
  shiftId?: string
  placeId: string
  description: string
}

// ---------------------------------------------------------------------------
// Helpers & Validators
// ---------------------------------------------------------------------------

export function personRefKey(ref: OpsPersonRef): string {
  if (ref.kind === 'MEMBER') {
    return `m_${ref.uid}`
  }
  return `w_${ref.workerId}`
}

export function isSamePersonRef(a: OpsPersonRef, b: OpsPersonRef): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'MEMBER' && b.kind === 'MEMBER') return a.uid === b.uid
  if (a.kind === 'WORKER' && b.kind === 'WORKER') return a.workerId === b.workerId
  return false
}

export function validateDutyRoleKey(key: unknown): string {
  if (typeof key !== 'string' || !DUTY_ROLE_KEY_REGEX.test(key.trim())) {
    throw new Error(
      `Invalid duty role key "${String(key)}". Key must match ^[a-z0-9_]{1,40}$.`,
    )
  }
  return key.trim()
}

export function validateShiftMinutes(
  startMinute: unknown,
  endMinute: unknown,
): {
  startMinute: number
  endMinute: number
  durationMinutes: number
  crossesMidnight: boolean
} {
  if (
    typeof startMinute !== 'number' ||
    !Number.isInteger(startMinute) ||
    startMinute < 0 ||
    startMinute > 1439
  ) {
    throw new Error('startMinute must be an integer between 0 and 1439.')
  }
  if (
    typeof endMinute !== 'number' ||
    !Number.isInteger(endMinute) ||
    endMinute < 0 ||
    endMinute > 1439
  ) {
    throw new Error('endMinute must be an integer between 0 and 1439.')
  }

  if (startMinute === endMinute) {
    throw new Error(
      'Shift duration cannot be 0 minutes or 24 hours (startMinute and endMinute cannot be equal).',
    )
  }

  let durationMinutes: number
  let crossesMidnight: boolean
  if (endMinute > startMinute) {
    durationMinutes = endMinute - startMinute
    crossesMidnight = false
  } else {
    durationMinutes = 1440 - startMinute + endMinute
    crossesMidnight = true
  }

  if (durationMinutes <= 0 || durationMinutes >= 1440) {
    throw new Error('Shift duration must be strictly greater than 0 and less than 24 hours.')
  }

  return { startMinute, endMinute, durationMinutes, crossesMidnight }
}

const DAY_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/

export function validateDayKey(key: unknown, label = 'dayKey'): string {
  if (typeof key !== 'string' || !DAY_KEY_REGEX.test(key.trim())) {
    throw new Error(`${label} must be formatted as YYYY-MM-DD.`)
  }
  return key.trim()
}

export function addDaysToKey(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days, 12, 0, 0))
  return dt.toISOString().slice(0, 10)
}

export function daysBetweenKeys(startKey: string, endKey: string): number {
  const [y1, m1, d1] = startKey.split('-').map(Number)
  const [y2, m2, d2] = endKey.split('-').map(Number)
  const t1 = Date.UTC(y1, m1 - 1, d1)
  const t2 = Date.UTC(y2, m2 - 1, d2)
  return Math.round((t2 - t1) / 86400000)
}

export function enumerateDayKeys(startKey: string, endKey: string): string[] {
  const count = daysBetweenKeys(startKey, endKey)
  if (count < 0) return []
  const res: string[] = []
  for (let i = 0; i <= count; i++) {
    res.push(addDaysToKey(startKey, i))
  }
  return res
}

export function validateRosterPeriod(
  periodStart: unknown,
  periodEnd: unknown,
): { periodStart: string; periodEnd: string; dayCount: number } {
  const start = validateDayKey(periodStart, 'periodStart')
  const end = validateDayKey(periodEnd, 'periodEnd')
  const diff = daysBetweenKeys(start, end)
  if (diff < 0) {
    throw new Error(`periodEnd (${end}) cannot be earlier than periodStart (${start}).`)
  }
  const dayCount = diff + 1
  if (dayCount > MAX_ROSTER_PERIOD_DAYS) {
    throw new Error(
      `Roster period cannot exceed ${MAX_ROSTER_PERIOD_DAYS} days (requested ${dayCount} days).`,
    )
  }
  return { periodStart: start, periodEnd: end, dayCount }
}

export function validateRosterAssignments(
  assignments: unknown,
): OpsRosterAssignment[] {
  if (!Array.isArray(assignments)) {
    throw new Error('assignments must be an array.')
  }
  if (assignments.length > MAX_ROSTER_ASSIGNMENTS) {
    throw new Error(
      `Draft assignments cannot exceed ${MAX_ROSTER_ASSIGNMENTS} items (provided ${assignments.length}).`,
    )
  }

  return assignments.map((item, idx) => {
    if (!item || typeof item !== 'object') {
      throw new Error(`Assignment at index ${idx} is not an object.`)
    }
    const raw = item as Record<string, unknown>
    const id = typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : `assign_${idx + 1}`
    const dayKey = validateDayKey(raw.dayKey, `Assignment[${idx}].dayKey`)
    const shiftId = typeof raw.shiftId === 'string' && raw.shiftId.trim() ? raw.shiftId.trim() : ''
    if (!shiftId) {
      throw new Error(`Assignment at index ${idx} is missing a shiftId.`)
    }
    const dutyRoleKey =
      typeof raw.dutyRoleKey === 'string' && raw.dutyRoleKey.trim()
        ? raw.dutyRoleKey.trim()
        : ''
    if (!dutyRoleKey) {
      throw new Error(`Assignment at index ${idx} is missing dutyRoleKey.`)
    }

    const kind = raw.kind as OpsAssignmentKind
    if (!OPS_ASSIGNMENT_KINDS.includes(kind)) {
      throw new Error(
        `Assignment at index ${idx} has invalid kind "${String(raw.kind)}".`,
      )
    }

    const pRef = raw.personRef as OpsPersonRef | undefined
    if (
      !pRef ||
      typeof pRef !== 'object' ||
      (pRef.kind !== 'MEMBER' && pRef.kind !== 'WORKER')
    ) {
      throw new Error(
        `Assignment at index ${idx} has invalid personRef. Kind must be MEMBER or WORKER.`,
      )
    }

    let personRef: OpsPersonRef
    if (pRef.kind === 'MEMBER') {
      if (typeof pRef.uid !== 'string' || !pRef.uid.trim()) {
        throw new Error(`Assignment at index ${idx} personRef.uid is required for MEMBER.`)
      }
      personRef = { kind: 'MEMBER', uid: pRef.uid.trim() }
    } else {
      if (typeof pRef.workerId !== 'string' || !pRef.workerId.trim()) {
        throw new Error(
          `Assignment at index ${idx} personRef.workerId is required for WORKER.`,
        )
      }
      personRef = { kind: 'WORKER', workerId: pRef.workerId.trim() }
    }

    return {
      id,
      dayKey,
      shiftId,
      dutyRoleKey,
      kind,
      personRef,
    }
  })
}

// ---------------------------------------------------------------------------
// Warning Computation
// ---------------------------------------------------------------------------

export interface ComputeRosterWarningsOptions {
  assignments: OpsRosterAssignment[]
  shifts: OpsShift[]
  coverageRules?: OpsCoverageRule[]
  workerSkills?: Record<string, string[]>
  complianceItems?: OpsComplianceItem[]
  personStaffGroups?: Record<string, string>
  approvedLeaves?: Array<{ personRef: OpsPersonRef; dayKey: string }>
  otherPublishedRosters?: Array<{
    rosterId: string
    assignments: OpsRosterAssignment[]
    periodStart: string
    periodEnd: string
  }>
  timeZone?: string
  period?: { periodStart: string; periodEnd: string }
}

function getZonedMinuteEpoch(
  dayKey: string,
  minute: number,
  timeZone: string,
): number {
  const [y, m, d] = dayKey.split('-').map(Number)
  const hour = Math.floor(minute / 60)
  const min = minute % 60
  return zonedTimeToUtc(y, m, d, hour, min, 0, 0, timeZone).getTime()
}

export function computeRosterWarnings(
  options: ComputeRosterWarningsOptions,
): OpsRosterWarning[] {
  const warnings: OpsRosterWarning[] = []
  const tz = resolveWorkspaceTimeZone(options.timeZone)
  const shiftMap = new Map<string, OpsShift>()
  for (const s of options.shifts) {
    shiftMap.set(s.id, s)
  }

  // Calculate intervals for assignments
  interface AssignmentInterval {
    assignment: OpsRosterAssignment
    startMs: number
    endMs: number
  }

  const intervals: AssignmentInterval[] = []
  for (const a of options.assignments) {
    const shift = shiftMap.get(a.shiftId)
    if (!shift) continue
    const startMs = getZonedMinuteEpoch(a.dayKey, shift.startMinute, tz)
    const endMinute =
      typeof shift.endMinute === 'number'
        ? shift.endMinute
        : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
    const endDayKey =
      endMinute <= shift.startMinute
        ? addDaysToKey(a.dayKey, 1)
        : a.dayKey
    const endMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)
    intervals.push({ assignment: a, startMs, endMs })
  }

  // 1. OVERLAP: Same person, overlapping shift instants (across midnight and days)
  const byPerson = new Map<string, AssignmentInterval[]>()
  for (const item of intervals) {
    const key = personRefKey(item.assignment.personRef)
    const list = byPerson.get(key) || []
    list.push(item)
    byPerson.set(key, list)
  }

  for (const personIntervals of byPerson.values()) {
    for (let i = 0; i < personIntervals.length; i++) {
      for (let j = i + 1; j < personIntervals.length; j++) {
        const a1 = personIntervals[i]
        const a2 = personIntervals[j]
        // Overlap: A.start < B.end && B.start < A.end
        if (a1.startMs < a2.endMs && a2.startMs < a1.endMs) {
          warnings.push({
            code: 'OVERLAP',
            message: `Person is scheduled for overlapping shifts on ${a1.assignment.dayKey} and ${a2.assignment.dayKey}.`,
            personRef: a1.assignment.personRef,
            dayKey: a1.assignment.dayKey,
            shiftId: a1.assignment.shiftId,
            conflictingDayKey: a2.assignment.dayKey,
            conflictingShiftId: a2.assignment.shiftId,
          })
        }
      }
    }
  }

  // 2. DOUBLE_BOOKED: Same person in another published roster of the same workspace at the same time
  if (options.otherPublishedRosters && options.otherPublishedRosters.length > 0) {
    const otherIntervals: Array<AssignmentInterval & { rosterId: string }> = []
    for (const other of options.otherPublishedRosters) {
      for (const oa of other.assignments) {
        const shift = shiftMap.get(oa.shiftId)
        if (!shift) continue
        const startMs = getZonedMinuteEpoch(oa.dayKey, shift.startMinute, tz)
        const endMinute =
          typeof shift.endMinute === 'number'
            ? shift.endMinute
            : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
        const endDayKey =
          endMinute <= shift.startMinute
            ? addDaysToKey(oa.dayKey, 1)
            : oa.dayKey
        const endMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)
        otherIntervals.push({
          assignment: oa,
          startMs,
          endMs,
          rosterId: other.rosterId,
        })
      }
    }

    for (const item of intervals) {
      const pKey = personRefKey(item.assignment.personRef)
      for (const other of otherIntervals) {
        if (personRefKey(other.assignment.personRef) === pKey) {
          if (item.startMs < other.endMs && other.startMs < item.endMs) {
            warnings.push({
              code: 'DOUBLE_BOOKED',
              message: `Person is double-booked with another published roster (${other.rosterId}) on ${other.assignment.dayKey}.`,
              personRef: item.assignment.personRef,
              dayKey: item.assignment.dayKey,
              shiftId: item.assignment.shiftId,
              otherRosterId: other.rosterId,
              conflictingDayKey: other.assignment.dayKey,
              conflictingShiftId: other.assignment.shiftId,
            })
          }
        }
      }
    }
  }

  // 3. MISSING_COVER: Fewer than minCount
  if (options.coverageRules && options.period) {
    const days = enumerateDayKeys(
      options.period.periodStart,
      options.period.periodEnd,
    )
    for (const rule of options.coverageRules) {
      if (rule.archived) continue
      for (const d of days) {
        const count = options.assignments.filter((a) => {
          if (
            a.dayKey !== d ||
            a.shiftId !== rule.shiftId ||
            a.dutyRoleKey !== rule.dutyRoleKey ||
            a.kind !== 'SHIFT'
          ) {
            return false
          }
          if (rule.staffGroup) {
            const pKey = personRefKey(a.personRef)
            const group = options.personStaffGroups?.[pKey]
            // A MEMBER with no linked worker doc has no group and does not count
            return group === rule.staffGroup
          }
          return true
        }).length

        if (count < rule.minCount) {
          const groupDetail = rule.staffGroup ? ` (${rule.staffGroup})` : ''
          warnings.push({
            code: 'MISSING_COVER',
            message: `Shift on ${d} has insufficient coverage for duty role "${rule.dutyRoleKey}"${groupDetail}: ${count}/${rule.minCount}.`,
            placeId: rule.placeId,
            shiftId: rule.shiftId,
            dutyRoleKey: rule.dutyRoleKey,
            dayKey: d,
            minCount: rule.minCount,
            actualCount: count,
            staffGroup: rule.staffGroup ?? undefined,
          })
        }
      }
    }
  }

  // 4. SKILL_MISSING: Assigned person lacks requiredSkill
  if (options.coverageRules && options.workerSkills) {
    for (const rule of options.coverageRules) {
      if (rule.archived || !rule.requiredSkill) continue
      const reqSkill = rule.requiredSkill.trim()
      const matchingAssignments = options.assignments.filter(
        (a) => a.shiftId === rule.shiftId && a.dutyRoleKey === rule.dutyRoleKey,
      )

      for (const ma of matchingAssignments) {
        const pKey = personRefKey(ma.personRef)
        const skills = options.workerSkills[pKey] || []
        if (!skills.includes(reqSkill)) {
          warnings.push({
            code: 'SKILL_MISSING',
            message: `Person assigned to shift on ${ma.dayKey} lacks required skill "${reqSkill}".`,
            placeId: rule.placeId,
            shiftId: rule.shiftId,
            dutyRoleKey: rule.dutyRoleKey,
            dayKey: ma.dayKey,
            personRef: ma.personRef,
            requiredSkill: reqSkill,
          })
        }
      }
    }
  }

  // 4b. LEGAL_ROLE_MISSING: Shift lacks required legalRoleKey
  if (options.coverageRules) {
    const days = options.period
      ? enumerateDayKeys(options.period.periodStart, options.period.periodEnd)
      : Array.from(new Set(options.assignments.map((a) => a.dayKey)))
    const complianceItems = options.complianceItems || []
    for (const rule of options.coverageRules) {
      if (rule.archived || !rule.legalRoleKey) continue
      const reqRole = rule.legalRoleKey.trim()

      for (const d of days) {
        const shiftAssignments = options.assignments.filter(
          (a) => a.dayKey === d && a.shiftId === rule.shiftId && a.kind === 'SHIFT',
        )

        const hasRoleHolder = shiftAssignments.some((a) =>
          holdsLegalRole(complianceItems, a.personRef, reqRole, d),
        )

        if (!hasRoleHolder) {
          warnings.push({
            code: 'LEGAL_ROLE_MISSING',
            message: `Shift on ${d} lacks required legal role "${reqRole}".`,
            placeId: rule.placeId,
            shiftId: rule.shiftId,
            dayKey: d,
            legalRoleKey: reqRole,
          })
        }
      }
    }
  }

  // 5. ON_LEAVE: An approved leave covers the assignment
  if (options.approvedLeaves && options.approvedLeaves.length > 0) {
    for (const a of options.assignments) {
      const isLeave = options.approvedLeaves.some(
        (l) => isSamePersonRef(l.personRef, a.personRef) && l.dayKey === a.dayKey,
      )
      if (isLeave) {
        warnings.push({
          code: 'ON_LEAVE',
          message: `Person is scheduled for shift on ${a.dayKey} while on approved leave.`,
          personRef: a.personRef,
          dayKey: a.dayKey,
          shiftId: a.shiftId,
        })
      }
    }
  }

  return warnings
}

export function validateTimeHHmm(time: string): string {
  if (!time || typeof time !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time.trim())) {
    throw new Error(`Time "${time}" must be in HH:mm format (00:00 - 23:59)`)
  }
  return time.trim()
}

export function validateLeaveRange(
  startDayKey: string,
  endDayKey?: string,
): { startDayKey: string; endDayKey: string; dayKeys: string[] } {
  validateDayKey(startDayKey, 'startDayKey')
  const end = endDayKey ? validateDayKey(endDayKey, 'endDayKey') : startDayKey
  if (end < startDayKey) {
    throw new Error(`End day ${end} must be >= start day ${startDayKey}`)
  }
  const count = daysBetweenKeys(startDayKey, end) + 1
  if (count > 31) {
    throw new Error(`Leave duration cannot exceed 31 days (requested ${count} days)`)
  }
  const dayKeys = enumerateDayKeys(startDayKey, end)
  return { startDayKey, endDayKey: end, dayKeys }
}

export interface ParsedAttendanceRow {
  rowNumber: number
  identifier: string
  dayKey: string
  shiftCode: string
  status: OpsAttendanceStatus
  actualIn?: string
  actualOut?: string
}

export interface AttendanceCsvParseResult {
  totalRows: number
  validRows: number
  rows: ParsedAttendanceRow[]
  errorRows: Array<{ rowNumber: number; field: string; message: string }>
}

export function parseAttendanceCsv(csvText: string): AttendanceCsvParseResult {
  if (!csvText || typeof csvText !== 'string') {
    throw new Error('CSV text is required')
  }

  const parsed = parseCsvText(csvText, {
    maxByteLength: 1024 * 1024,
    maxDataRows: OPS_ATTENDANCE_MAX_CSV_ROWS,
  })

  if (parsed.header.length === 0) {
    return { totalRows: 0, validRows: 0, rows: [], errorRows: [] }
  }

  const headers = parsed.header.map((h) => h.toLowerCase().replace(/[\s_-]+/g, ''))

  const idIdx = headers.findIndex(
    (h) => h === 'workercode' || h === 'email' || h === 'worker' || h === 'id' || h === 'identifier',
  )
  const dateIdx = headers.findIndex((h) => h === 'date' || h === 'daykey' || h === 'day')
  const shiftIdx = headers.findIndex((h) => h === 'shiftcode' || h === 'shift')
  const statusIdx = headers.findIndex((h) => h === 'status')
  const inIdx = headers.findIndex((h) => h === 'in' || h === 'actualin')
  const outIdx = headers.findIndex((h) => h === 'out' || h === 'actualout')

  if (idIdx === -1 || dateIdx === -1 || shiftIdx === -1 || statusIdx === -1) {
    throw new Error('CSV must include headers for worker_code/email, date, shift_code, and status')
  }

  const rows: ParsedAttendanceRow[] = []
  const errorRows: Array<{ rowNumber: number; field: string; message: string }> = []

  for (const item of parsed.rows) {
    const rowNumber = item.rowNumber
    const tokens = item.cells

    const identifier = tokens[idIdx] || ''
    const rawDate = tokens[dateIdx] || ''
    const shiftCode = tokens[shiftIdx] || ''
    const rawStatus = (tokens[statusIdx] || '').toUpperCase()
    const rawIn = inIdx !== -1 && tokens[inIdx] ? tokens[inIdx] : undefined
    const rawOut = outIdx !== -1 && tokens[outIdx] ? tokens[outIdx] : undefined

    if (!identifier) {
      errorRows.push({ rowNumber, field: 'identifier', message: 'Missing worker_code or email' })
      continue
    }

    let dayKey: string
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      dayKey = rawDate
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(rawDate)) {
      const parts = rawDate.split('-')
      dayKey = `${parts[2]}-${parts[1]}-${parts[0]}`
    } else {
      errorRows.push({ rowNumber, field: 'date', message: `Invalid date format: ${rawDate}` })
      continue
    }

    try {
      validateDayKey(dayKey, 'dayKey')
    } catch (e: unknown) {
      errorRows.push({
        rowNumber,
        field: 'date',
        message: e instanceof Error ? e.message : String(e),
      })
      continue
    }

    if (!shiftCode) {
      errorRows.push({ rowNumber, field: 'shift_code', message: 'Missing shift code' })
      continue
    }

    if (!(OPS_ATTENDANCE_STATUSES as readonly string[]).includes(rawStatus)) {
      errorRows.push({ rowNumber, field: 'status', message: `Invalid attendance status: ${rawStatus}` })
      continue
    }

    let actualIn: string | undefined
    let actualOut: string | undefined

    if (rawIn) {
      try {
        actualIn = validateTimeHHmm(rawIn)
      } catch (e: unknown) {
        errorRows.push({
          rowNumber,
          field: 'in',
          message: e instanceof Error ? e.message : String(e),
        })
        continue
      }
    }

    if (rawOut) {
      try {
        actualOut = validateTimeHHmm(rawOut)
      } catch (e: unknown) {
        errorRows.push({
          rowNumber,
          field: 'out',
          message: e instanceof Error ? e.message : String(e),
        })
        continue
      }
    }

    rows.push({
      rowNumber,
      identifier,
      dayKey,
      shiftCode,
      status: rawStatus as OpsAttendanceStatus,
      actualIn,
      actualOut,
    })
  }

  return {
    totalRows: parsed.rows.length,
    validRows: rows.length,
    rows,
    errorRows,
  }
}

// ---------------------------------------------------------------------------
// Relief Candidates Helper (R39 G2.b / SO-P104)
// ---------------------------------------------------------------------------

export interface OpsReliefCandidatePerson {
  personRef: OpsPersonRef
  name: string
  role?: string
  active?: boolean
}

export interface ListReliefCandidatesOptions {
  candidates: readonly OpsReliefCandidatePerson[]
  dayKey: string
  shiftId: string
  assignments?: readonly OpsRosterAssignment[]
  approvedLeaves?: readonly OpsRosterChange[]
  absentPersonRef?: OpsPersonRef
}

export interface OpsReliefCandidate {
  personRef: OpsPersonRef
  name: string
  isOffToday: boolean
  role?: string
}

/**
 * Pure helper to list eligible candidates for relief assignment.
 * R39 G2.b / SO-P104.
 *
 * Filters candidates:
 * - Excludes inactive candidates (active === false).
 * - Excludes the absent person being relieved (absentPersonRef).
 * - Enforces role ceiling: excludes members holding VIEWER or READONLY workspace roles.
 * - Excludes candidates on approved LEAVE covering the target dayKey.
 * - Excludes candidates already double-booked on that shift (same dayKey and shiftId).
 *
 * Sorts eligible candidates:
 * - Free / off-today candidates first (has no assigned shifts on target dayKey).
 * - Then alphabetically by candidate name.
 */
export function listReliefCandidates(
  options: ListReliefCandidatesOptions,
): OpsReliefCandidate[] {
  const {
    candidates,
    dayKey,
    shiftId,
    assignments = [],
    approvedLeaves = [],
    absentPersonRef,
  } = options

  const results: OpsReliefCandidate[] = []
  const seenPersonKeys = new Set<string>()

  for (const candidate of candidates) {
    if (candidate.active === false) {
      continue
    }

    const key = personRefKey(candidate.personRef)
    if (seenPersonKeys.has(key)) {
      continue
    }

    // Role ceiling: VIEWER or READONLY cannot be assigned relief
    if (candidate.role === 'VIEWER' || candidate.role === 'READONLY') {
      continue
    }

    // Exclude the absent person being replaced
    if (absentPersonRef && isSamePersonRef(candidate.personRef, absentPersonRef)) {
      continue
    }

    // Exclude candidate on approved leave on target dayKey
    const onLeave = approvedLeaves.some((l) => {
      if (l.type && l.type !== 'LEAVE') return false
      if (l.state && l.state !== 'APPROVED') return false
      const leavePerson =
        l.requesterPersonRef ||
        (l.requesterUid ? { kind: 'MEMBER' as const, uid: l.requesterUid } : null)
      if (!leavePerson || !isSamePersonRef(leavePerson, candidate.personRef)) {
        return false
      }
      const start = l.dayKey || ''
      const end = l.endDayKey || l.dayKey || ''
      return dayKey >= start && dayKey <= end
    })

    if (onLeave) {
      continue
    }

    // Check double-booking on this exact shift-day
    const isDoubleBooked = assignments.some(
      (a) =>
        a.dayKey === dayKey &&
        a.shiftId === shiftId &&
        isSamePersonRef(a.personRef, candidate.personRef),
    )

    if (isDoubleBooked) {
      continue
    }

    // Check whether candidate has any other shift on this dayKey
    const hasShiftToday = assignments.some(
      (a) => a.dayKey === dayKey && isSamePersonRef(a.personRef, candidate.personRef),
    )

    seenPersonKeys.add(key)
    results.push({
      personRef: candidate.personRef,
      name: candidate.name,
      isOffToday: !hasShiftToday,
      ...(candidate.role ? { role: candidate.role } : {}),
    })
  }

  // Sort: off-today first, then alphabetically by name
  results.sort((a, b) => {
    if (a.isOffToday !== b.isOffToday) {
      return a.isOffToday ? -1 : 1
    }
    const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    if (cmp !== 0) return cmp
    return personRefKey(a.personRef).localeCompare(personRefKey(b.personRef))
  })

  return results
}

// ---------------------------------------------------------------------------
// QR Attendance & Peer Proxy Pure Helpers (R39 F3 / SO-P109)
// ---------------------------------------------------------------------------

/**
 * Returns false for PENDING or REJECTED peer marks.
 * Used by all attendance readers (wages, relief planning, coverage, UI).
 */
export function isAttendanceEffective(
  doc?: { source?: string | null; proxyState?: string | null } | null,
): boolean {
  if (!doc) return false
  if (doc.proxyState === 'PENDING' || doc.proxyState === 'REJECTED') {
    return false
  }
  if (doc.source === 'PROXY_PEER' && doc.proxyState !== 'APPROVED') {
    return false
  }
  return true
}

/**
 * Quote token format used by client quotes (/q/<48 hex>).
 * Place stickers use 22-char base64url tokens.
 */
export const QUOTE_TOKEN_REGEX = /^[0-9a-f]{48}$/

export function isQuoteToken(token: unknown): boolean {
  return typeof token === 'string' && QUOTE_TOKEN_REGEX.test(token.trim().toLowerCase())
}

/**
 * Determines check-in attendance status (PRESENT or LATE).
 * Exactly start + graceMinutes = PRESENT; after start + graceMinutes = LATE.
 */
export function selfCheckInStatus(
  now: Date | number,
  shiftStart: Date | number,
  graceMinutes: number = 15,
): 'PRESENT' | 'LATE' {
  const nowMs = typeof now === 'number' ? now : now.getTime()
  const startMs = typeof shiftStart === 'number' ? shiftStart : shiftStart.getTime()
  const graceMs = Math.max(0, graceMinutes) * 60 * 1000
  return nowMs <= startMs + graceMs ? 'PRESENT' : 'LATE'
}

export interface OpsSelfCheckInMatch {
  assignment: OpsRosterAssignment
  shift: OpsShift
  dayKey: string
  shiftStartMs: number
  shiftEndMs: number
  windowStartMs: number
  windowEndMs: number
}

export interface FindSelfCheckInAssignmentOptions {
  now: Date | number
  assignments: OpsRosterAssignment[]
  shifts: OpsShift[]
  personRef: OpsPersonRef
  timeZone?: string
}

/**
 * Finds the assignment in the latest published roster covering now.
 * Shift window: start - 60 min to end.
 * Overnight shifts on their start day, checking today's and yesterday's dayKey.
 */
export function findSelfCheckInAssignment(
  options: FindSelfCheckInAssignmentOptions,
): OpsSelfCheckInMatch | null {
  const nowMs = typeof options.now === 'number' ? options.now : options.now.getTime()
  const tz = resolveWorkspaceTimeZone(options.timeZone)
  const todayKey = formatZonedDateKey(new Date(nowMs), tz)
  const yesterdayKey = addDaysToKey(todayKey, -1)
  const allowedDayKeys = new Set([todayKey, yesterdayKey])

  const shiftMap = new Map<string, OpsShift>()
  for (const s of options.shifts) {
    if (!s.archived) {
      shiftMap.set(s.id, s)
    }
  }

  const candidates: OpsSelfCheckInMatch[] = []
  for (const a of options.assignments) {
    if (a.kind !== 'SHIFT') continue
    if (!allowedDayKeys.has(a.dayKey)) continue
    if (!isSamePersonRef(a.personRef, options.personRef)) continue

    const shift = shiftMap.get(a.shiftId)
    if (!shift) continue

    const shiftStartMs = getZonedMinuteEpoch(a.dayKey, shift.startMinute, tz)
    const windowStartMs = shiftStartMs - 60 * 60 * 1000

    const endMinute =
      typeof shift.endMinute === 'number'
        ? shift.endMinute
        : (shift.startMinute + (shift.durationMinutes ?? 0)) % 1440
    const crossesMidnight = endMinute <= shift.startMinute
    const endDayKey = crossesMidnight ? addDaysToKey(a.dayKey, 1) : a.dayKey
    const shiftEndMs = getZonedMinuteEpoch(endDayKey, endMinute, tz)
    const windowEndMs = shiftEndMs

    if (nowMs >= windowStartMs && nowMs <= windowEndMs) {
      candidates.push({
        assignment: a,
        shift,
        dayKey: a.dayKey,
        shiftStartMs,
        shiftEndMs,
        windowStartMs,
        windowEndMs,
      })
    }
  }

  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0]

  candidates.sort((a, b) => {
    const aActive = nowMs >= a.shiftStartMs && nowMs <= a.shiftEndMs
    const bActive = nowMs >= b.shiftStartMs && nowMs <= b.shiftEndMs
    if (aActive && !bActive) return -1
    if (!aActive && bActive) return 1
    return Math.abs(nowMs - a.shiftStartMs) - Math.abs(nowMs - b.shiftStartMs)
  })

  return candidates[0]
}

// ---------------------------------------------------------------------------
// Roster Version Diff Helper (R39 C6.a4)
// ---------------------------------------------------------------------------

export interface RosterVersionChange {
  before: OpsRosterAssignment
  after: OpsRosterAssignment
  personKey: string
  dayKey: string
}

export interface RosterVersionDiff {
  added: OpsRosterAssignment[]
  removed: OpsRosterAssignment[]
  changed: RosterVersionChange[]
}

export function diffRosterVersions(
  prev: { assignments?: OpsRosterAssignment[] } | null | undefined,
  next: { assignments?: OpsRosterAssignment[] } | null | undefined,
): RosterVersionDiff {
  const prevAssignments = prev?.assignments || []
  const nextAssignments = next?.assignments || []

  const added: OpsRosterAssignment[] = []
  const removed: OpsRosterAssignment[] = []
  const changed: RosterVersionChange[] = []

  const prevById = new Map<string, OpsRosterAssignment>()
  const prevByPersonDayKind = new Map<string, OpsRosterAssignment[]>()
  for (const a of prevAssignments) {
    if (a.id) prevById.set(a.id, a)
    const pdk = `${personRefKey(a.personRef)}__${a.dayKey}__${a.kind}`
    const list = prevByPersonDayKind.get(pdk) || []
    list.push(a)
    prevByPersonDayKind.set(pdk, list)
  }

  const matchedPrevIds = new Set<string>()

  for (const nextA of nextAssignments) {
    const pKey = personRefKey(nextA.personRef)
    if (nextA.id && prevById.has(nextA.id)) {
      const prevA = prevById.get(nextA.id)!
      matchedPrevIds.add(prevA.id)
      const isDifferent =
        prevA.shiftId !== nextA.shiftId ||
        prevA.dutyRoleKey !== nextA.dutyRoleKey ||
        prevA.kind !== nextA.kind ||
        prevA.dayKey !== nextA.dayKey ||
        personRefKey(prevA.personRef) !== pKey
      if (isDifferent) {
        changed.push({
          before: prevA,
          after: nextA,
          personKey: pKey,
          dayKey: nextA.dayKey,
        })
      }
      continue
    }

    const pdk = `${pKey}__${nextA.dayKey}__${nextA.kind}`
    const candidates = prevByPersonDayKind.get(pdk) || []
    const unmatchedPrev = candidates.find((p) => !matchedPrevIds.has(p.id))

    if (unmatchedPrev) {
      matchedPrevIds.add(unmatchedPrev.id)
      const isDifferent =
        unmatchedPrev.shiftId !== nextA.shiftId ||
        unmatchedPrev.dutyRoleKey !== nextA.dutyRoleKey
      if (isDifferent) {
        changed.push({
          before: unmatchedPrev,
          after: nextA,
          personKey: pKey,
          dayKey: nextA.dayKey,
        })
      }
    } else {
      const anyCandidate = prevAssignments.find(
        (p) => !matchedPrevIds.has(p.id) && personRefKey(p.personRef) === pKey && p.dayKey === nextA.dayKey,
      )
      if (anyCandidate) {
        matchedPrevIds.add(anyCandidate.id)
        changed.push({
          before: anyCandidate,
          after: nextA,
          personKey: pKey,
          dayKey: nextA.dayKey,
        })
      } else {
        added.push(nextA)
      }
    }
  }

  for (const prevA of prevAssignments) {
    if (!matchedPrevIds.has(prevA.id)) {
      removed.push(prevA)
    }
  }

  return { added, removed, changed }
}



